import axios from 'axios';
import { getTelegramInitData } from '../lib/telegram';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export const getAssetUrl = (value?: string): string | undefined => {
  if (!value) return undefined;
  if (/^https?:\/\//i.test(value) || value.startsWith('data:')) return value;
  return new URL(value, API_BASE_URL).toString();
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use(config => {
  const initData = getTelegramInitData();
  if (initData) config.headers.set('X-Telegram-Init-Data', initData);
  return config;
});

// Интерцептор: при network-ошибке даём понятное сообщение
api.interceptors.response.use(
  res => res,
  err => {
    if (err.code === 'ECONNABORTED' || !err.response) {
      return Promise.reject(new Error('Сервер недоступен. Проверьте подключение.'));
    }
    const msg = err.response?.data?.error || err.message || 'Ошибка запроса';
    return Promise.reject(new Error(msg));
  }
);

export interface User {
  id: number;
  telegram_id: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  created_at: string;
}

export interface Exercise {
  id: number;
  workout_id: number;
  name: string;
  sets?: number;
  reps?: string;
  weight?: string;
  notes?: string;
}

export interface WorkoutAnalysis {
  exercises: Exercise[];
  summary: string;
  totalVolume?: string;
  duration?: string;
  intensity?: string;
  kind?: 'running' | 'strength' | 'unknown';
  confidence?: number;
  warnings?: string[];
  activityType?: string;
  date?: string;
  startTime?: string;
  durationSeconds?: number;
  distanceKm?: number;
  calories?: number;
  steps?: number;
  hrAvg?: number;
  hrMax?: number;
  paceAvgSecPerKm?: number;
  paceBestSecPerKm?: number;
  speedAvgKmh?: number;
  cadenceAvg?: number;
  cadenceMax?: number;
  strideLengthCm?: number;
  groundContactAvgMs?: number;
  groundContactMinMs?: number;
  verticalOscAvgCm?: number;
  verticalOscMaxCm?: number;
  balanceLeftPct?: number;
  balanceRightPct?: number;
  trainingStressAerobic?: number;
  trainingStressAnaerobic?: number;
  recoveryTimeHours?: number;
}

export interface Workout {
  id: number;
  user_id: number;
  date: string;
  screenshot_url?: string;
  ai_analysis?: WorkoutAnalysis | null;
  exercises: Exercise[];
  running_stats?: RunningStats | null;
  source?: string;
  score?: WorkoutScore | null;
  created_at: string;
}

export interface RunnerProfile {
  user_id: number;
  age: number;
  sex: 'male' | 'female' | 'other';
  weight_kg: number;
  experience: 'beginner' | 'intermediate' | 'advanced';
  updated_at: string;
}

export interface RunnerGoal {
  user_id: number;
  goal_type: 'five_k' | 'ten_k' | 'half_marathon' | 'lower_hr' | 'consistency';
  target_date?: string;
  target_value?: number;
  weekly_days: number;
  updated_at: string;
}

export interface ReadinessData {
  date: string;
  score: number | null;
  level: string;
  recommendation: string;
  checkin: {
    sleep_hours: number;
    fatigue: number;
    soreness: number;
    mood: number;
  } | null;
  factors: Array<{ label: string; value: string; score: number; max: number }>;
}

export interface WorkoutScore {
  total: number;
  level: 'отлично' | 'сильно' | 'стабильно' | 'развиваем' | 'внимание';
  personalized: boolean;
  provisional: boolean;
  components: Array<{
    key: 'cardio' | 'technique' | 'load' | 'efficiency' | 'data';
    label: string;
    score: number;
    max: number;
    insight: string;
  }>;
  summary: string;
}

export interface CalendarPlan {
  id: number;
  user_id: number;
  date: string;
  ai_recommendation?: {
    date: string;
    exercises: Array<{ name: string; sets: number; reps: string; notes?: string }>;
    focus: string;
    notes: string;
  } | null;
  status: string;
  created_at: string;
}

export interface CalendarData {
  plans: CalendarPlan[];
  workouts: Workout[];
}

export interface FeedbackData {
  feedback: string;
  recommendations?: string[];
  progress?: string;
}

export interface Feedback {
  id: number;
  user_id: number;
  workout_id?: number;
  feedback_text: FeedbackData | null;
  feedback_type: string;
  created_at: string;
}

export interface WeeklyTrainingPlan {
  startDate: string;
  endDate: string;
  title: string;
  summary: string;
  days: Array<{
    date: string;
    type: 'run' | 'recovery' | 'strength' | 'rest';
    title: string;
    focus: string;
    durationMinutes?: number;
    distanceKm?: number;
    intensity: 'низкая' | 'средняя' | 'высокая';
    details: string[];
  }>;
  coachNotes: string[];
  needsRefresh?: boolean;
}

export interface WorkoutStats {
  total: number;
  thisWeek: number;
  thisMonth: number;
  totalExercises: number;
  lastWorkoutDate?: string;
}

// API методы
export const getUser = async (
  telegramId: number,
  username?: string,
  firstName?: string,
  lastName?: string
): Promise<User> => {
  const params: Record<string, string> = {};
  if (username) params.username = username;
  if (firstName) params.firstName = firstName;
  if (lastName) params.lastName = lastName;
  const r = await api.get(`/api/user/${telegramId}`, { params });
  return r.data;
};

export const ensureUser = async (
  telegramId: number,
  username?: string,
  firstName?: string,
  lastName?: string
): Promise<User> => {
  const r = await api.post('/api/user', { telegramId, username, firstName, lastName });
  return r.data;
};

export const getWorkouts = async (userId: number, limit?: number, offset?: number): Promise<Workout[]> => {
  const r = await api.get(`/api/workouts/${userId}`, { params: { limit, offset } });
  return r.data;
};

export interface WorkoutFilters {
  activityType?: string;
  distanceMin?: number;
  distanceMax?: number;
  intensity?: string;
}

export const getFilteredWorkouts = async (
  userId: number,
  limit?: number,
  offset?: number,
  filters: WorkoutFilters = {}
): Promise<Workout[]> => {
  const r = await api.get(`/api/workouts/${userId}`, { params: { limit, offset, ...filters } });
  return r.data;
};

export const getWorkout = async (workoutId: number): Promise<Workout> => {
  const r = await api.get(`/api/workout/${workoutId}`);
  return r.data;
};

export const createWorkout = async (
  userId: number,
  date: string,
  exercises: Partial<Exercise>[],
  source?: string
): Promise<Workout> => {
  const r = await api.post('/api/workouts', { userId, date, exercises, source });
  return r.data;
};

export const getStats = async (userId: number): Promise<WorkoutStats> => {
  const r = await api.get(`/api/stats/${userId}`);
  return r.data;
};

export const getRunnerProfile = async (userId: number): Promise<RunnerProfile | null> => {
  const r = await api.get(`/api/profile/${userId}`);
  return r.data;
};

export const saveRunnerProfile = async (
  userId: number,
  profile: Pick<RunnerProfile, 'age' | 'sex' | 'weight_kg' | 'experience'>
): Promise<RunnerProfile> => {
  const r = await api.put(`/api/profile/${userId}`, profile);
  return r.data;
};

export const getRunnerGoal = async (userId: number): Promise<RunnerGoal | null> => {
  const r = await api.get(`/api/goal/${userId}`);
  return r.data;
};

export const saveRunnerGoal = async (
  userId: number,
  goal: Omit<RunnerGoal, 'user_id' | 'updated_at'>
): Promise<RunnerGoal> => {
  const r = await api.put(`/api/goal/${userId}`, goal);
  return r.data;
};

export const getReadiness = async (userId: number): Promise<ReadinessData> => {
  const r = await api.get(`/api/readiness/${userId}`);
  return r.data;
};

export const saveReadiness = async (
  userId: number,
  values: { sleep_hours: number; fatigue: number; soreness: number; mood: number }
): Promise<ReadinessData> => {
  const r = await api.put(`/api/readiness/${userId}`, values);
  return r.data;
};

export const getCalendar = async (userId: number, year: number, month: number): Promise<CalendarData> => {
  const r = await api.get(`/api/calendar/${userId}/${year}/${month}`);
  return r.data;
};

export const getFeedback = async (userId: number, limit?: number, offset?: number): Promise<Feedback[]> => {
  const r = await api.get(`/api/feedback/${userId}`, { params: { limit, offset } });
  return r.data;
};

export const getAIRecommendation = async (userId: number, targetDate: string): Promise<CalendarPlan> => {
  const r = await api.post('/api/ai/recommend', { userId, targetDate });
  return r.data;
};

export const getAIFeedback = async (userId: number, workoutId: number): Promise<FeedbackData> => {
  const r = await api.post('/api/ai/feedback', { userId, workoutId });
  return r.data;
};

export const getWeeklyPlan = async (userId: number): Promise<WeeklyTrainingPlan | null> => {
  const r = await api.get(`/api/ai/week-plan/${userId}`);
  return r.data;
};

export const generateWeeklyPlan = async (userId: number): Promise<WeeklyTrainingPlan> => {
  const r = await api.post('/api/ai/week-plan', { userId }, { timeout: 210_000 });
  return r.data;
};

export const updatePlanStatus = async (planId: number, status: string): Promise<void> => {
  await api.patch(`/api/calendar/${planId}`, { status });
};

export interface ValidationIssue {
  field: string;
  level: 'warning' | 'error';
  message: string;
}

export interface WorkoutImportDraft {
  id: number;
  status: string;
  expiresAt: string;
  screenshotUrl: string;
  analysis: WorkoutAnalysis;
  validation: ValidationIssue[];
  imageParts: number;
  requiresChanges: boolean;
}

export const analyzeWorkoutImage = async (
  userId: number,
  imageFile: File
): Promise<WorkoutImportDraft> => {
  const formData = new FormData();
  formData.append('image', imageFile);
  formData.append('userId', String(userId));
  const r = await api.post('/api/workouts/analyze', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 210_000
  });
  return r.data;
};

export const confirmWorkoutImport = async (
  userId: number,
  importId: number,
  analysis: WorkoutAnalysis,
  acceptWarnings = false
): Promise<Workout & { feedback?: FeedbackData }> => {
  const r = await api.post(`/api/workout-imports/${importId}/confirm`, {
    userId,
    analysis,
    acceptWarnings
  }, { timeout: 120_000 });
  return r.data;
};

export const cancelWorkoutImport = async (userId: number, importId: number): Promise<void> => {
  await api.delete(`/api/workout-imports/${importId}`, { params: { userId } });
};

export const checkHealth = async (): Promise<boolean> => {
  try {
    await api.get('/health', { timeout: 5000 });
    return true;
  } catch {
    return false;
  }
};

export interface RunningStats {
  id: number;
  workout_id: number;
  source: string;
  activity_type?: string;
  start_time?: string;
  duration_seconds?: number;
  distance_km?: number;
  calories?: number;
  steps?: number;
  hr_avg?: number;
  hr_max?: number;
  pace_avg_sec_per_km?: number;
  pace_best_sec_per_km?: number;
  speed_avg_kmh?: number;
  cadence_avg?: number;
  cadence_max?: number;
  stride_length_cm?: number;
  ground_contact_avg_ms?: number;
  ground_contact_min_ms?: number;
  vertical_osc_avg_cm?: number;
  vertical_osc_max_cm?: number;
  balance_left_pct?: number;
  balance_right_pct?: number;
  hr_recovery_bpm?: number;
  training_stress_aerobic?: number;
  training_stress_anaerobic?: number;
  recovery_time_hours?: number;
  ground_contact_rating?: string;
  zones_json?: Record<string, number> | null;
  intensity_zones_json?: Record<string, number> | null;
  pace_breakdown_json?: Array<{ range: string; minutes: number; quality: string }> | null;
  km_splits_json?: any;
  created_at: string;
}

export interface AnalyticsData {
  summary: {
    workouts: number;
    distanceKm: number;
    durationSeconds: number;
    avgPaceSecPerKm: number | null;
    avgHeartRate: number | null;
    avgScore: number | null;
  };
  trends: Array<{
    workoutId: number;
    date: string;
    activityType?: string;
    distanceKm?: number;
    durationSeconds?: number;
    paceAvgSecPerKm?: number;
    hrAvg?: number;
    cadenceAvg?: number;
    recoveryTimeHours?: number;
    aerobicStress?: number;
    score?: number;
  }>;
  latestComparison: {
    workoutId: number;
    comparableCount: number;
    distanceKm?: number;
    paceDeltaSec: number | null;
    heartRateDelta: number | null;
    cadenceDelta: number | null;
  } | null;
  scoreTrend: Array<{ workoutId: number; date: string; score: number }>;
  trainingLoad: {
    current: number;
    previous: number;
    ratio: number | null;
    monotony: number;
    strain: number;
    currentDistanceKm: number;
    previousDistanceKm: number;
    status: string;
    recommendation: string;
  };
  personalRecords: {
    distances: Array<{
      distanceKm: number;
      workoutId: number;
      date: string;
      paceSecPerKm: number;
      estimatedTimeSeconds: number;
    }>;
    longest: { workoutId: number; date: string; distanceKm: number } | null;
    bestScore: { workoutId: number; score: number } | null;
  };
  planAdherence: {
    score: number | null;
    completed: number;
    planned: number;
    items: Array<{
      date: string;
      status: 'completed' | 'missed' | 'upcoming';
      score: number | null;
      focus: string;
      actual?: { workoutId: number; distanceKm?: number; durationSeconds?: number } | null;
    }>;
  };
}

export const getAnalytics = async (userId: number): Promise<AnalyticsData> => {
  const r = await api.get(`/api/analytics/${userId}`);
  return r.data;
};

export const deleteWorkout = async (userId: number, workoutId: number): Promise<void> => {
  await api.delete(`/api/workout/${workoutId}`, { params: { userId } });
};

export const deleteUserData = async (userId: number): Promise<void> => {
  await api.delete(`/api/user-data/${userId}`);
};

export const exportUserData = async (userId: number): Promise<void> => {
  const response = await api.get(`/api/data-export/${userId}`, { responseType: 'blob' });
  const url = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = `runlab-export-${new Date().toISOString().slice(0, 10)}.json`;
  link.click();
  URL.revokeObjectURL(url);
};

export const getRunningStats = async (workoutId: number): Promise<RunningStats | null> => {
  try {
    const r = await api.get(`/api/running-stats/${workoutId}`);
    return r.data;
  } catch {
    return null;
  }
};

export default api;
